import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MagnifyingGlass, Sparkle, Flask as FlaskConical, ArrowRight, X, FunnelSimple,
  Clock, Eye, FloppyDisk, Lightning, ChartLine, Carrot, Receipt, Trash, Plus,
  Package, Star, Storefront,
} from "@phosphor-icons/react";
import { Wand2 } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { api, fmtINR } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const ENHANCEMENTS = [
  { key: "cost", label: "Optimize for Cost", icon: <Receipt size={14} weight="bold" /> },
  { key: "protein", label: "Make High-Protein", icon: <Lightning size={14} weight="bold" /> },
  { key: "time", label: "Reduce Cook Time", icon: <Clock size={14} weight="bold" /> },
  { key: "scale", label: "Scale for Commercial", icon: <ChartLine size={14} weight="bold" /> },
  { key: "shelf_life", label: "Improve Shelf Life", icon: <Package size={14} weight="bold" /> },
];

const SMART_TAG_STYLE = {
  "High Margin": "bg-[#0A3B24] text-white border-[#0A3B24]",
  "Low Cost": "bg-[#FDF9EE] text-[#A87C0E] border-[#D4AF37]",
  "Quick Prep": "bg-blue-50 text-blue-800 border-blue-200",
  "High Protein": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Commercial Friendly": "bg-purple-50 text-purple-800 border-purple-200",
  "Cloud Kitchen Ready": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Mass Production": "bg-amber-50 text-amber-800 border-amber-200",
  "Long Shelf Life": "bg-teal-50 text-teal-800 border-teal-200",
  "Premium": "bg-[#D4AF37]/10 text-[#A87C0E] border-[#D4AF37]/40",
  "Kid Friendly": "bg-pink-50 text-pink-800 border-pink-200",
  "Diet Friendly": "bg-lime-50 text-lime-800 border-lime-200",
};

export default function DiscoverPage() {
  const { isAdmin, openLogin } = useAuth();
  const [meta, setMeta] = useState(null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({
    cuisine: "Any", category: "Any", difficulty: "Any", cost_tier: "Any",
    cook_time_max: 0, dietary: [], functional_category: "any",
    commercial_flags: [],
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [preview, setPreview] = useState(null); // recipe being viewed
  const [structuring, setStructuring] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editPrimary, setEditPrimary] = useState([]);
  const [editSecondary, setEditSecondary] = useState([]);
  const [editSteps, setEditSteps] = useState([]);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // Load filter meta on mount
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/discovery/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [isAdmin]);

  const runSearch = async () => {
    if (!isAdmin) return openLogin();
    if (!query || query.trim().length < 2) {
      toast.error("Type at least 2 characters to search");
      return;
    }
    setSearching(true);
    setResults([]);
    try {
      const payload = {
        query: query.trim(),
        cuisine: filters.cuisine === "Any" ? null : filters.cuisine,
        category: filters.category === "Any" ? null : filters.category,
        difficulty: filters.difficulty === "Any" ? null : filters.difficulty,
        cost_tier: filters.cost_tier === "Any" ? null : filters.cost_tier,
        cook_time_max: filters.cook_time_max ? parseInt(filters.cook_time_max, 10) : null,
        dietary: filters.dietary.length ? filters.dietary : null,
        commercial_flags: filters.commercial_flags.length ? filters.commercial_flags : null,
        functional_category: filters.functional_category && filters.functional_category !== "any"
          ? filters.functional_category : null,
        n_results: 8,
      };
      const { data } = await api.post("/discovery/search", payload);
      setResults(data.results || []);
      setLastQuery(query.trim());
      if ((data.results || []).length === 0) toast.warning("No matches — try a different query or relax filters");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  };

  const onPreview = async (r) => {
    // Open immediately with whatever stub we have
    setPreview(r);
    setEditName(r.dish_name);
    setEditPrimary((r.primary_ingredients || []).map((i) => ({ ...i })));
    setEditSecondary((r.secondary_ingredients || []).map((i) => ({ ...i })));
    setEditSteps((r.steps || []).map((s) => ({ ...s })));
    setEditNotes(r.notes || "");
    // If we don't yet have ingredients/steps, fetch the structured version via LLM
    if (!r._structured || (r.primary_ingredients || []).length === 0) {
      setStructuring(true);
      try {
        const { data } = await api.post("/discovery/structure", { recipe: r });
        const full = data.recipe || {};
        setPreview((p) => ({ ...(p || r), ...full, _structured: true }));
        setEditName(full.dish_name || r.dish_name);
        setEditPrimary((full.primary_ingredients || []).map((i) => ({ ...i })));
        setEditSecondary((full.secondary_ingredients || []).map((i) => ({ ...i })));
        setEditSteps((full.steps || []).map((s) => ({ ...s })));
        setEditNotes(full.notes || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not structure recipe — try again");
      } finally { setStructuring(false); }
    }
  };

  // One-click "Functional Foods" launcher — pre-fills query + selects the
  // functional_category and a few sensible dietary tags, then runs search.
  const launchFunctional = (cat) => {
    setFilters((f) => ({
      ...f,
      functional_category: cat.key,
      dietary: Array.from(new Set([...f.dietary, ...(cat.tags || [])])),
    }));
    const seed = cat.query_seed || cat.label;
    setQuery(seed);
    // Run search after state flush
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="discover-search-btn"]');
      if (btn) btn.click();
    }, 60);
  };

  const closePreview = () => { setPreview(null); };

  const updateIng = (which, idx, patch) => {
    const setter = which === "primary" ? setEditPrimary : setEditSecondary;
    const list = which === "primary" ? editPrimary : editSecondary;
    const next = [...list]; next[idx] = { ...next[idx], ...patch };
    setter(next);
  };
  const removeIng = (which, idx) => {
    const setter = which === "primary" ? setEditPrimary : setEditSecondary;
    const list = which === "primary" ? editPrimary : editSecondary;
    setter(list.filter((_, i) => i !== idx));
  };
  const addIng = (which) => {
    const setter = which === "primary" ? setEditPrimary : setEditSecondary;
    const list = which === "primary" ? editPrimary : editSecondary;
    setter([...list, { id: crypto.randomUUID(), name: "", quantity: 0, unit: "g", rate_per_kg: 0 }]);
  };
  const updateStep = (idx, patch) => {
    const next = [...editSteps]; next[idx] = { ...next[idx], ...patch }; setEditSteps(next);
  };
  const removeStep = (idx) => setEditSteps(editSteps.filter((_, i) => i !== idx));
  const addStep = () => setEditSteps([...editSteps, {
    step_no: editSteps.length + 1, description: "", time_min: 0,
  }]);

  const buildPayloadRecipe = () => ({
    ...preview,
    dish_name: editName,
    primary_ingredients: editPrimary,
    secondary_ingredients: editSecondary,
    steps: editSteps.map((s, i) => ({ ...s, step_no: i + 1 })),
    notes: editNotes,
  });

  const onEnhance = async (goal) => {
    if (!preview) return;
    setEnhancing(true);
    try {
      toast.info(`Asking GPT-4o-mini to ${ENHANCEMENTS.find(e => e.key === goal)?.label.toLowerCase()}…`);
      const { data } = await api.post("/discovery/enhance", { recipe: buildPayloadRecipe(), goal });
      const r = data.recipe;
      setEditName(r.dish_name || editName);
      setEditPrimary(r.primary_ingredients || []);
      setEditSecondary(r.secondary_ingredients || []);
      setEditSteps((r.steps || []).map((s, i) => ({ ...s, step_no: i + 1 })));
      setEditNotes(r.notes || editNotes);
      toast.success(r._optimization_note || "Recipe enhanced");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enhancement failed");
    } finally { setEnhancing(false); }
  };

  const onImport = async (afterAction) => {
    if (!preview) return;
    setImporting(true);
    try {
      const payload = {
        recipe: buildPayloadRecipe(),
        source_query: lastQuery,
        suggest_rnd_factors: afterAction === "rnd",
      };
      const { data } = await api.post("/discovery/import", payload);
      // Stash suggested factors so RnDPage can pick them up after navigation
      if (afterAction === "rnd" && data.suggested_factors) {
        try {
          localStorage.setItem(
            `kostra:rnd:auto-factors:${data.id}`,
            JSON.stringify(data.suggested_factors),
          );
        } catch {}
      }
      toast.success(`Saved "${data.recipe.dish_name}" to Recipe Library`);
      setPreview(null);
      if (afterAction === "cost") {
        window.location.href = `/?edit=${data.id}`;
      } else if (afterAction === "rnd") {
        window.location.href = `/rnd?base=${data.id}&auto_factors=1`;
      } else if (afterAction === "library") {
        window.location.href = "/library";
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  // Guest CTA -------------------------------------------------------------
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <Sparkle size={14} weight="fill" /> Recipe Discovery Engine
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              Discover. Cost. Optimize. <br className="hidden md:block" />
              <span className="text-[#D4AF37]">All in one search.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Search any dish or goal — KOSTRA's AI engine returns 8 production-ready recipes,
              fully structured for the costing calculator and R&amp;D Lab. Admin login required.
            </p>
            <Button onClick={() => openLogin()} className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
                    data-testid="discover-guest-login-btn">
              Admin login to start discovering
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Header />

      {/* Search hero */}
      <div className="bg-gradient-to-b from-[#0A3B24] via-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-14">
          <div className="text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <Sparkle size={14} weight="fill" /> Recipe Discovery Engine · Instant Search · powered by AI
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              Discover any recipe. <br className="hidden md:block" />
              <span className="text-[#D4AF37]">Cost it. Optimize it. Ship it.</span>
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Type a dish, a goal, or an ingredient combo. Results in under 1.5 seconds —
              full ingredient list and steps stream in when you preview.
            </p>
          </div>

          {/* Search bar */}
          <div className="mt-8 max-w-3xl mx-auto" data-testid="discover-searchbar">
            <div className="bg-white rounded-full shadow-2xl flex items-center pl-5 pr-2 py-2 gap-3 border-2 border-[#D4AF37]/40 focus-within:border-[#D4AF37] transition">
              <MagnifyingGlass size={20} weight="bold" className="text-[#0A3B24] shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                placeholder='Try "high protein nachos", "paneer + oats snack", or "low cost vegan dessert"'
                className="flex-1 bg-transparent outline-none text-[#111827] text-base placeholder:text-[#9CA3AF] font-medium"
                data-testid="discover-query-input"
              />
              <Button
                onClick={runSearch}
                disabled={searching}
                className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold rounded-full h-11 px-6"
                data-testid="discover-search-btn"
              >
                {searching ? "Searching…" : (<><Sparkle size={14} weight="fill" className="mr-2" /> Discover</>)}
              </Button>
            </div>

            {/* Filter toggle */}
            <div className="mt-3 flex items-center justify-center gap-3 text-xs">
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-white/85 hover:text-white border border-white/20 hover:border-white/40 px-3 py-1.5 rounded-full"
                data-testid="discover-filters-toggle"
              >
                <FunnelSimple size={13} weight="bold" />
                {filtersOpen ? "Hide filters" : "Show filters"}
              </button>
              {(filters.cuisine !== "Any" || filters.category !== "Any" ||
                filters.difficulty !== "Any" || filters.cost_tier !== "Any" ||
                filters.dietary.length > 0 || filters.cook_time_max > 0 ||
                (filters.commercial_flags || []).length > 0 ||
                (filters.functional_category && filters.functional_category !== "any")) && (
                <span className="text-[#D4AF37]">Filters active</span>
              )}
            </div>
          </div>

          {/* Functional Foods chip row */}
          {meta?.functional_categories && (
            <div className="mt-7" data-testid="discover-functional-row">
              <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3 inline-flex items-center gap-1.5 justify-center w-full">
                <Sparkle size={11} weight="fill" /> Functional Foods · One-tap discovery
              </div>
              <div className="flex flex-nowrap gap-2 overflow-x-auto pb-2 max-w-5xl mx-auto px-2 scrollbar-thin"
                   style={{ scrollbarWidth: "thin" }}>
                {meta.functional_categories
                  .filter((c) => c.key !== "any")
                  .map((c) => {
                    const active = filters.functional_category === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => launchFunctional(c)}
                        className={`shrink-0 px-3.5 py-1.5 rounded-full border-2 text-xs font-semibold whitespace-nowrap transition-all ${
                          active
                            ? "bg-[#D4AF37] text-[#0A3B24] border-[#D4AF37] shadow-lg"
                            : "bg-white/10 text-white border-white/25 hover:bg-white/20 hover:border-white/50"
                        }`}
                        data-testid={`functional-chip-${c.key}`}
                      >
                        {c.label}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-8 pb-12 space-y-6">
        {/* Filter card */}
        {filtersOpen && meta && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="kostra-card p-5"
            data-testid="discover-filters-panel"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-3">
                <Label className="kostra-data-label mb-1.5 block">Cuisine</Label>
                <Select value={filters.cuisine} onValueChange={(v) => setFilters({ ...filters, cuisine: v })}>
                  <SelectTrigger className="h-11" data-testid="filter-cuisine"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.cuisines.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="kostra-data-label mb-1.5 block">Category</Label>
                <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                  <SelectTrigger className="h-11" data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="kostra-data-label mb-1.5 block">Difficulty</Label>
                <Select value={filters.difficulty} onValueChange={(v) => setFilters({ ...filters, difficulty: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.difficulties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="kostra-data-label mb-1.5 block">Cost Tier</Label>
                <Select value={filters.cost_tier} onValueChange={(v) => setFilters({ ...filters, cost_tier: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.cost_tiers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="kostra-data-label mb-1.5 block">Max Cook Time (min)</Label>
                <Input
                  type="number" min="0" step="5"
                  value={filters.cook_time_max || ""}
                  onChange={(e) => setFilters({ ...filters, cook_time_max: parseInt(e.target.value || 0, 10) })}
                  placeholder="any"
                  className="h-11 font-mono-num"
                  data-testid="filter-cooktime"
                />
              </div>
              <div className="md:col-span-12">
                <Label className="kostra-data-label mb-2 block">Functional Food Category</Label>
                <Select
                  value={filters.functional_category || "any"}
                  onValueChange={(v) => setFilters({ ...filters, functional_category: v })}
                >
                  <SelectTrigger className="h-11" data-testid="filter-functional"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {(meta.functional_categories || []).map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#4B5563] mt-1.5">
                  Targets a specific health benefit — KOSTRA briefs the AI with the right ingredients
                  (probiotics, omega-3, low-GI, adaptogens, …) for that category.
                </p>
              </div>
              <div className="md:col-span-12">
                <Label className="kostra-data-label mb-2 block">Dietary Tags</Label>
                <div className="flex flex-wrap gap-2" data-testid="filter-dietary">
                  {meta.dietary_tags.map((d) => {
                    const active = filters.dietary.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setFilters({
                          ...filters,
                          dietary: active ? filters.dietary.filter((x) => x !== d) : [...filters.dietary, d],
                        })}
                        className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold transition ${
                          active
                            ? "bg-[#0A3B24] text-white border-[#0A3B24]"
                            : "bg-white text-[#0A3B24] border-[#0A3B24]/30 hover:border-[#0A3B24]"}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              {(meta.commercial_flags || []).length > 0 && (
                <div className="md:col-span-12">
                  <Label className="kostra-data-label mb-2 block inline-flex items-center gap-1.5">
                    <Storefront size={12} weight="bold" /> Smart Business Filters
                  </Label>
                  <div className="flex flex-wrap gap-2" data-testid="filter-commercial-flags">
                    {meta.commercial_flags.map((f) => {
                      const active = (filters.commercial_flags || []).includes(f);
                      return (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setFilters({
                            ...filters,
                            commercial_flags: active
                              ? filters.commercial_flags.filter((x) => x !== f)
                              : [...filters.commercial_flags, f],
                          })}
                          className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold transition ${
                            active
                              ? "bg-[#D4AF37] text-[#0A3B24] border-[#D4AF37] shadow"
                              : "bg-white text-[#0A3B24] border-[#D4AF37]/40 hover:border-[#D4AF37]"}`}
                          data-testid={`commercial-flag-${f.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {f}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[#4B5563] mt-1.5">
                    Filter for commercial-kitchen-ready recipes — high margin, cloud-kitchen friendly, fast moving, bulk production, retail pack.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Empty state / loader */}
        {!searching && results.length === 0 && (
          <div className="kostra-card p-10 text-center" data-testid="discover-empty">
            <Sparkle size={36} weight="duotone" className="mx-auto text-[#D4AF37]" />
            <div className="text-base font-semibold text-[#111827] mt-3">Type a search to discover recipes</div>
            <div className="text-xs text-[#4B5563] mt-1.5 max-w-md mx-auto">
              Examples: <em>"high protein vegan breakfast"</em>, <em>"low cost mango dessert"</em>,
              <em>"30-minute Asian noodle bowl"</em>.
            </div>
          </div>
        )}
        {searching && (
          <div className="kostra-card p-10 text-center" data-testid="discover-searching">
            <div className="inline-flex items-center gap-2 text-[#0A3B24]">
              <span className="h-3 w-3 rounded-full bg-[#D4AF37] animate-ping" />
              <span className="text-base font-semibold">Curating recipes for "{query}"…</span>
            </div>
            <div className="text-xs text-[#4B5563] mt-2">Instant catalog match — usually under 1 second.</div>
          </div>
        )}

        {/* Results grid */}
        {!searching && results.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="kostra-section-title text-xl">{results.length} results for <span className="text-[#0A3B24]">"{lastQuery}"</span></h2>
              <span className="text-xs text-[#4B5563]">Curated catalog · enriched with AI on Preview</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="discover-results">
              <AnimatePresence>
                {results.map((r, i) => (
                  <motion.div
                    key={r.slug}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.04 }}
                    className="kostra-card overflow-hidden flex flex-col hover:shadow-lg hover:border-[#D4AF37]/50 transition-all"
                    data-testid={`discover-card-${i}`}
                  >
                    <div className="aspect-video w-full bg-[#F9FAFB] overflow-hidden relative">
                      {r.image && (
                        <img src={r.image} alt={r.dish_name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                      )}
                      {/* Relevance score badge top-right */}
                      {r.relevance_score > 0 && (
                        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0A3B24] text-white text-xs font-bold shadow-lg"
                             data-testid={`discover-score-${i}`}>
                          <Star size={11} weight="fill" className="text-[#D4AF37]" />
                          <span className="font-mono-num">{r.relevance_score}</span>
                        </div>
                      )}
                      {/* Cost tier badge bottom-left */}
                      <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-[#0A3B24] text-[10px] font-bold uppercase tracking-wider shadow">
                        {r.cost_tier}
                      </div>
                    </div>
                    <div className="p-4 md:p-5 flex flex-col flex-1">
                      <h3 className="font-display text-lg text-[#0A3B24] leading-snug">{r.dish_name}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#4B5563]">
                        <span className="inline-flex items-center gap-1"><Carrot size={11} /> {r.cuisine}</span>
                        <span>·</span>
                        <span>{r.category}</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><Clock size={11} /> {r.cook_time_min}m</span>
                        <span>·</span>
                        <span>{r.difficulty}</span>
                      </div>

                      {/* Smart business intelligence row */}
                      {(r.cost_estimate_per_portion > 0 || r.suggested_price_max > 0) && (
                        <div className="mt-3 grid grid-cols-3 gap-2 p-2.5 rounded-lg bg-[#FDF9EE]/60 border border-[#D4AF37]/20"
                             data-testid={`discover-intel-${i}`}>
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">Cost</div>
                            <div className="font-mono-num font-bold text-[#0A3B24] text-sm">
                              ₹{Number(r.cost_estimate_per_portion).toFixed(0)}
                            </div>
                            <div className="text-[9px] text-[#4B5563]">per portion</div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">Sell at</div>
                            <div className="font-mono-num font-bold text-[#0A3B24] text-sm">
                              ₹{Number(r.suggested_price_min).toFixed(0)}–{Number(r.suggested_price_max).toFixed(0)}
                            </div>
                            <div className="text-[9px] text-[#4B5563]">menu price</div>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-[#4B5563] font-bold">Margin</div>
                            <div className="font-mono-num font-bold text-emerald-700 text-sm">
                              {r.estimated_margin_pct ? `${r.estimated_margin_pct.toFixed(0)}%` : "—"}
                            </div>
                            <div className="text-[9px] text-[#4B5563]">est. gross</div>
                          </div>
                        </div>
                      )}

                      <p className="text-sm text-[#4B5563] mt-3 line-clamp-2">{r.description}</p>

                      {/* Smart tags — business intelligence */}
                      {(r.smart_tags || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`discover-smart-tags-${i}`}>
                          {(r.smart_tags || []).slice(0, 4).map((t) => (
                            <span key={t}
                                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border-2 font-bold ${
                                    SMART_TAG_STYLE[t] || "bg-[#FDF9EE] text-[#A87C0E] border-[#D4AF37]/40"}`}>
                              {t}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-auto pt-4 grid grid-cols-2 gap-2">
                        <Button onClick={() => onPreview(r)} variant="outline"
                                className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                                data-testid={`discover-preview-${i}`}>
                          <Eye size={14} weight="bold" className="mr-1.5" /> Preview
                        </Button>
                        <Button onClick={() => onPreview(r)}
                                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                                data-testid={`discover-quick-import-${i}`}>
                          <FloppyDisk size={14} weight="bold" className="mr-1.5" /> Import
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </>
        )}
      </main>

      {/* Preview / Import dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="discover-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#0A3B24]">
                  Review &amp; Import
                </DialogTitle>
                <p className="text-xs text-[#4B5563]">
                  {preview.cuisine} · {preview.category} · {preview.cook_time_min}m · serves {preview.num_portions}
                </p>
              </DialogHeader>

              {/* Smart Intelligence Strip */}
              {(preview.cost_estimate_per_portion > 0 || preview.relevance_score > 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="discover-preview-intel">
                  <IntelStat icon={<Star size={13} weight="fill" className="text-[#D4AF37]" />}
                             label="Relevance" value={`${preview.relevance_score}/100`} />
                  <IntelStat icon={<Receipt size={13} weight="bold" className="text-[#0A3B24]" />}
                             label="Cost / portion" value={`₹${Number(preview.cost_estimate_per_portion).toFixed(0)}`} />
                  <IntelStat icon={<Storefront size={13} weight="bold" className="text-[#0A3B24]" />}
                             label="Sell at"
                             value={`₹${Number(preview.suggested_price_min).toFixed(0)}–${Number(preview.suggested_price_max).toFixed(0)}`} />
                  <IntelStat icon={<ChartLine size={13} weight="bold" className="text-emerald-700" />}
                             label="Est. margin"
                             value={preview.estimated_margin_pct ? `${preview.estimated_margin_pct.toFixed(0)}%` : "—"} />
                </div>
              )}

              {/* Smart tags */}
              {(preview.smart_tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(preview.smart_tags || []).map((t) => (
                    <span key={t}
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border-2 font-bold ${
                            SMART_TAG_STYLE[t] || "bg-[#FDF9EE] text-[#A87C0E] border-[#D4AF37]/40"}`}>
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* Structuring loading banner */}
              {structuring && (
                <div className="rounded-xl border border-[#D4AF37]/40 bg-[#FDF9EE]/70 p-3 flex items-center gap-3"
                     data-testid="discover-structuring">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#D4AF37] animate-ping" />
                  <span className="text-sm font-semibold text-[#0A3B24]">
                    Structuring recipe with AI…
                  </span>
                  <span className="text-xs text-[#4B5563]">
                    Generating ingredient list, quantities, and cook steps (~3-6s)
                  </span>
                </div>
              )}

              {/* AI Enhancement bar */}
              <div className="rounded-xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#FDF9EE] to-white p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 size={14} className="text-[#D4AF37]" />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A3B24]">AI Enhancement</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ENHANCEMENTS.map((e) => (
                    <Button key={e.key} type="button" onClick={() => onEnhance(e.key)} disabled={enhancing}
                            variant="outline" size="sm"
                            className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#0A3B24] hover:text-white font-semibold text-xs"
                            data-testid={`enhance-${e.key}-btn`}>
                      {e.icon}<span className="ml-1.5">{e.label}</span>
                    </Button>
                  ))}
                  {enhancing && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#4B5563] ml-2">
                      <span className="h-2 w-2 rounded-full bg-[#D4AF37] animate-ping" /> Working…
                    </span>
                  )}
                </div>
              </div>

              {/* Editable fields */}
              <div className="space-y-5">
                <div>
                  <Label className="kostra-data-label mb-1.5 block">Dish Name</Label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-11"
                         data-testid="discover-edit-name" />
                </div>

                <IngredientsEditor
                  title="Primary Ingredients"
                  items={editPrimary}
                  onUpdate={(idx, p) => updateIng("primary", idx, p)}
                  onRemove={(idx) => removeIng("primary", idx)}
                  onAdd={() => addIng("primary")}
                  testidPrefix="discover-primary"
                />
                <IngredientsEditor
                  title="Secondary Ingredients"
                  items={editSecondary}
                  onUpdate={(idx, p) => updateIng("secondary", idx, p)}
                  onRemove={(idx) => removeIng("secondary", idx)}
                  onAdd={() => addIng("secondary")}
                  testidPrefix="discover-secondary"
                />

                {/* Steps */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="kostra-section-title text-base">Cooking Steps</Label>
                    <Button onClick={addStep} variant="outline" size="sm"
                            className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold">
                      <Plus size={12} weight="bold" className="mr-1" /> Add Step
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {editSteps.map((s, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`discover-step-${i}`}>
                        <div className="col-span-1 pt-2.5 text-[#4B5563] font-mono-num font-bold text-sm">
                          {i + 1}.
                        </div>
                        <div className="col-span-8">
                          <Textarea
                            value={s.description}
                            onChange={(e) => updateStep(i, { description: e.target.value })}
                            className="min-h-[44px] resize-y"
                            rows={2}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number" min="0" step="0.5"
                            value={s.time_min}
                            onChange={(e) => updateStep(i, { time_min: parseFloat(e.target.value || 0) })}
                            className="h-11 font-mono-num"
                            placeholder="min"
                          />
                        </div>
                        <div className="col-span-1 flex justify-end pt-1">
                          <button type="button" onClick={() => removeStep(i)}
                                  className="h-9 w-9 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] inline-flex items-center justify-center">
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="kostra-data-label mb-1.5 block">Chef Notes</Label>
                  <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                            rows={3} data-testid="discover-edit-notes" />
                </div>
              </div>

              {/* Footer actions */}
              <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t border-[#E5E7EB] flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-[#4B5563]">
                  Saved as: <span className="text-[#0A3B24] font-semibold">{editName}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={() => onImport("library")} disabled={importing}
                          variant="outline"
                          className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                          data-testid="discover-import-library">
                    <FloppyDisk size={14} weight="bold" className="mr-1.5" /> Save to Library
                  </Button>
                  <Button onClick={() => onImport("cost")} disabled={importing}
                          className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold"
                          data-testid="discover-import-cost">
                    <Receipt size={14} weight="bold" className="mr-1.5" /> Send to Calculator
                  </Button>
                  <Button onClick={() => onImport("rnd")} disabled={importing}
                          className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                          data-testid="discover-import-rnd">
                    <FlaskConical size={14} className="mr-1.5" /> Send to R&amp;D
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function IntelStat({ icon, label, value }) {
  return (
    <div className="rounded-lg border border-[#D4AF37]/30 bg-[#FDF9EE]/40 p-3">
      <div className="inline-flex items-center gap-1.5 text-[#4B5563]">
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="mt-1 font-display text-base text-[#0A3B24] font-mono-num font-bold">{value}</div>
    </div>
  );
}

function IngredientsEditor({ title, items, onUpdate, onRemove, onAdd, testidPrefix }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="kostra-section-title text-base">{title}</Label>
        <Button onClick={onAdd} variant="outline" size="sm"
                className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold">
          <Plus size={12} weight="bold" className="mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-[#4B5563] py-3 px-3 rounded-md border border-dashed border-[#E5E7EB] bg-[#F9FAFB]">
            No items.
          </div>
        )}
        {items.map((it, i) => (
          <div key={it.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testidPrefix}-row-${i}`}>
            <div className="col-span-7">
              <Input value={it.name} onChange={(e) => onUpdate(i, { name: e.target.value })}
                     className="h-11" placeholder="Ingredient name" />
            </div>
            <div className="col-span-3">
              <div className="relative">
                <Input
                  type="number" min="0" step="0.1"
                  value={it.quantity}
                  onChange={(e) => onUpdate(i, { quantity: parseFloat(e.target.value || 0) })}
                  className="h-11 font-mono-num pr-10" placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[#4B5563] font-semibold pointer-events-none">
                  {it.unit || "g"}
                </span>
              </div>
            </div>
            <div className="col-span-1">
              <Select value={it.unit || "g"} onValueChange={(v) => onUpdate(i, { unit: v })}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="g">g</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="ml">ml</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1 flex justify-end">
              <button type="button" onClick={() => onRemove(i)}
                      className="h-9 w-9 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] inline-flex items-center justify-center">
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
